/**
 * procedureLayout.js — подбор раскладки и сценария под процедуру по словам
 * описания исследования и модальности. Чистая логика без импортов; проверяется
 * на бумажных случаях (tests/procedure_test.mjs).
 *
 * ЗАЧЕМ. Слова владельца из задания про рабочий процесс отделения: «раскладка
 * под процедуру». Врач открывает запись из рабочего списка — программа сама
 * ставит подходящую раскладку окон (MPR для томов, одно окно для снимков) и,
 * если процедура узнана по словам, — готовый сценарий из реестра
 * CLINICAL_WORKFLOWS (окно W/L + 3D-пресет; сам реестр живёт в
 * presetsManager, здесь только ВЫБОР ключа).
 *
 * ЧЕСТНОСТЬ ПОДБОРА. Подбор — по словам, а слова в описаниях пишут люди;
 * поэтому: (1) непонятое описание даёт ТОЛЬКО раскладку, без сценария, с
 * причиной «слова процедуры не распознаны»; (2) порядок правил закреплён от
 * частного к общему (коронарография раньше ангиографии, ТЭЛА раньше грудной
 * клетки) и ЗАКРЕПЛЁН ТЕСТОМ — переставит кто-нибудь правила, тест упадёт;
 * (3) применять или нет — решает врач галочкой, подбор ничего не навязывает.
 */

// Правила: от ЧАСТНОГО к ОБЩЕМУ. Каждое: модальности, слова (нижний регистр,
// ищутся подстрокой), ключ сценария из реестра, человеческое имя.
const ПРАВИЛА = [
  { мод: ['CT'], слова: ['коронар', 'coronar', 'кальциев', 'calcium scor', 'са-скор'], сценарий: 'workflow-ct-coronary', имя: 'Коронарография' },
  { мод: ['CT'], слова: ['тэла', 'тромбоэмбол', 'ctpa', 'pulmonary emb', 'лёгочн', 'легочн'], сценарий: 'workflow-ct-pe', имя: 'КТ ТЭЛА' },
  { мод: ['CT'], слова: ['ангио', 'angio', 'аорт', 'aorta', 'сосуд'], сценарий: 'workflow-ct-angio', имя: 'КТ Ангиография' },
  { мод: ['CT'], слова: ['голов', 'мозг', 'brain', 'head', 'инсульт', 'stroke', 'чмт'], сценарий: 'workflow-ct-brain', имя: 'КТ Мозг' },
  { мод: ['CT'], слова: ['грудн', 'chest', 'thorax', 'огк', 'лёгк', 'легк', 'lung'], сценарий: 'workflow-ct-chest', имя: 'КТ Грудная клетка' },
  { мод: ['CT'], слова: ['кост', 'bone', 'позвоноч', 'spine', 'перелом', 'сустав'], сценарий: 'workflow-ct-bone', имя: 'КТ Кости' },
  { мод: ['MR'], слова: ['голов', 'мозг', 'brain', 'head'], сценарий: 'workflow-mri-brain', имя: 'МРТ Мозг' },
];

const ПЛОСКИЕ = new Set(['CR', 'DX', 'MG', 'XA', 'RF', 'US', 'OT', 'SC']);
const ОБЪЁМНЫЕ = new Set(['CT', 'MR', 'PT', 'NM']);

/**
 * @param {{модальности?: string[], описание?: string}} ст
 * @returns {{раскладка: '1x1'|'mpr', сценарий: string|null, имя: string|null, почему: string}}
 */
export function подобратьПроцедуру(ст = {}) {
  const моды = (ст.модальности || []).map((м) => String(м).toUpperCase());
  const текст = String(ст.описание || '').toLowerCase();

  const объёмная = моды.some((м) => ОБЪЁМНЫЕ.has(м));
  const плоская = моды.some((м) => ПЛОСКИЕ.has(м));
  // Смешанное (PT+CT, CT+SR) считаем объёмным: SR/плёнка не отменяют том.
  const раскладка = объёмная ? 'mpr' : (плоская ? '1x1' : 'mpr');

  for (const п of ПРАВИЛА) {
    if (!п.мод.some((м) => моды.includes(м))) continue;
    const слово = п.слова.find((с) => текст.includes(с));
    if (слово) {
      return { раскладка, сценарий: п.сценарий, имя: п.имя, почему: `по слову «${слово}» в описании` };
    }
  }
  return {
    раскладка, сценарий: null, имя: null,
    почему: объёмная
      ? 'слова процедуры не распознаны — только раскладка MPR'
      : (плоская ? 'плоский снимок — одно окно, сценарии здесь не про это' : 'модальность неизвестна — раскладка MPR наугад'),
  };
}
